import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/useAuth";
import {
  getSupportTicketById,
  getAdminSupportTicketById,
  replyToSupportTicket,
  replyToSupportTicketAsAdmin,
  updateSupportTicketStatusAsAdmin,
} from "../lib/supportApi";

function getPublicIdFromPath() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  return parts[1] || "";
}



function getAdminFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get("admin") === "1";
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function humanizeStatus(status) {
  switch (status) {
    case "OPEN":
      return "Open";
    case "IN_REVIEW":
      return "In Review";
    case "WAITING_FOR_USER":
      return "Waiting for You";
    case "RESOLVED":
      return "Resolved";
    case "CLOSED":
      return "Closed";
    default:
      return status || "-";
  }
}

function humanizeSender(senderType, senderWallet = "", adminView = false) {
  switch (senderType) {
    case "USER":
      if (adminView) {
        return senderWallet
          ? `User • ${getExplorerLabel(senderWallet)}`
          : "User";
      }
      return "You";

    case "ADMIN":
      return "Kriaxon Support";

    case "SYSTEM":
      return "System";

    default:
      return senderType || "-";
  }
}

function humanizeIssueType(issueType) {
  switch (issueType) {
    case "DEPLOYMENT_ERROR":
      return "Problem Report";
    case "FEATURE_REQUEST":
      return "Feature Request";
    case "PAYMENT_ISSUE":
      return "Payment Issue";
    case "NETWORK_ERROR":
      return "Network Issue";
    case "CONTRACT_VERIFICATION":
      return "Verification Issue";
    case "BUG_REPORT":
      return "Bug Report";
    case "OTHER":
      return "General Help";
    default:
      return issueType || "General Help";
  }
}

function humanizePriority(priority) {
  switch (priority) {
    case "LOW":
      return "Low";
    case "MEDIUM":
      return "Medium";
    case "HIGH":
      return "High";
    case "URGENT":
      return "Urgent";
    default:
      return priority || "-";
  }
}

function getExplorerLabel(value) {
  if (!value) return "-";
  if (value.length <= 16) return value;
  return `${value.slice(0, 8)}...${value.slice(-6)}`;
}

export default function TicketDetail() {
  const publicId = useMemo(() => getPublicIdFromPath(), []);
  const { isAuthed, authReady, restoreLoading, address } = useAuth();
  const adminView = useMemo(() => getAdminFromQuery(), []);

  const [walletAddress, setWalletAddress] = useState("");

  useEffect(() => {
    if (isAuthed && address) {
      setWalletAddress(address.toLowerCase());
    } else {
      setWalletAddress("");
    }
  }, [isAuthed, address]);

  const [request, setRequest] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [error, setError] = useState("");
  const effectiveAdminView = adminView && !error.includes("Admin access is no longer available");
  const [replyError, setReplyError] = useState("");
  const [adminActionError, setAdminActionError] = useState("");
  const [adminStatus, setAdminStatus] = useState("");
  const adminBusy = replying || updatingStatus || loading;

  useEffect(() => {
    setRequest(null);
    setReplyMessage("");
    setError("");
    setReplyError("");
    setAdminActionError("");
    setAdminStatus("");
    setLoading(true);
  }, [isAuthed, address, publicId]);

  const loadRequest = useCallback(
    async (activeWallet) => {

      if (!authReady || restoreLoading) return;

      const normalizedWallet = activeWallet?.trim();

      if (!publicId) {
        setLoading(false);
        return;
      }

      if (!adminView && (!isAuthed || !normalizedWallet)) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        let result;

        if (adminView) {
          result = await getAdminSupportTicketById(publicId);
        } else {
          result = await getSupportTicketById(publicId, normalizedWallet);
        }

        setRequest(result.data || null);

      } catch (err) {
        console.error("Failed to fetch support request:", err);
        setRequest(null);

        const status = err?.response?.status;

        if (status === 403 && adminView) {
          setError("Admin access is no longer available for this session.");
        } else if (status === 401) {
          setError("Your session expired. Please sign in again.");
        } else if (status === 403) {
          setError("You do not have access to this ticket.");
        } else {
          setError(err.message || "Failed to fetch support request.");
        }
      } finally {
        setLoading(false);
      }
    },
    [publicId, adminView, isAuthed, authReady, restoreLoading]
  );

  useEffect(() => {
    if (adminView) {
      loadRequest(walletAddress || "");
      return;
    }

    if (walletAddress) {
      loadRequest(walletAddress);
    } else {
      setLoading(false);
    }
  }, [walletAddress, loadRequest, adminView]);

  useEffect(() => {
    if (request?.status) {
      setAdminStatus(request.status);
    }
  }, [request]);

  async function handleReply(event) {
    event.preventDefault();
    setReplyError("");
    setAdminActionError("");

    if (replying || updatingStatus) {
      return;
    }

    if (!publicId) {
      setReplyError("Ticket ID is missing.");
      return;
    }

    if (!adminView && !walletAddress.trim()) {
      setReplyError("Wallet address is required.");
      return;
    }

    if (replyMessage.trim().length < 2) {
      setReplyError("Reply must be at least 2 characters.");
      return;
    }

    try {
      setReplying(true);

      if (adminView) {
        const result = await replyToSupportTicketAsAdmin(
          publicId,
          replyMessage.trim()
        );

        setRequest(result.data || null);
        setReplyMessage("");
        setReplyError("");
        return;
      }

      const result = await replyToSupportTicket(
        publicId,
        walletAddress.trim(),
        replyMessage.trim()
      );

      setRequest(result.data);
      setReplyMessage("");
    } catch (err) {
      console.error("Failed to send reply:", err);
      setReplyError(err.message || "Failed to send reply.");
    } finally {
      setReplying(false);
    }
  }

  async function handleAdminStatusUpdate() {
    setAdminActionError("");
    setReplyError("");

    if (replying || updatingStatus) {
      return;
    }

    if (!adminView) return;

    if (!publicId) {
      setAdminActionError("Ticket ID is missing.");
      return;
    }

    if (!adminStatus) {
      setAdminActionError("Select a valid status.");
      return;
    }

    try {
      setUpdatingStatus(true);

      const result = await updateSupportTicketStatusAsAdmin(
        publicId,
        adminStatus
      );

      setRequest(result.data || null);
    } catch (err) {
      console.error("Failed to update ticket status:", err);
      setAdminActionError(
        err.message || "Failed to update ticket status."
      );
    } finally {
      setUpdatingStatus(false);
    }
  }

  if (!authReady || restoreLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <p style={styles.muted}>Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <a href="/help-center" style={styles.backLink}>
          ← Back to Support Center
        </a>

        <h1 style={styles.title}>Support Request</h1>

        {effectiveAdminView ? (
          <div style={styles.card}>
            <div style={styles.requestId}>Admin View</div>
            <div style={styles.muted}>
              You are viewing this ticket as a privileged support operator.
            </div>

            {walletAddress ? (
              <div
                style={{
                  marginTop: "10px",
                  color: "#c7d3f7",
                  fontSize: "14px",
                }}
              >
                <strong>User Wallet:</strong> {walletAddress}
              </div>
            ) : null}
          </div>
        ) : (
          <div style={styles.card}>
            <div style={styles.requestId}>Authenticated View</div>
            <div style={styles.muted}>
              Ticket access is tied to your current signed-in wallet.
            </div>
          </div>
        )}

        {loading ? (
          <p style={styles.muted}>Loading request...</p>
        ) : error ? (
          <div style={styles.card}>
            <div style={styles.error}>{error}</div>

            <div style={{ marginTop: "14px" }}>
              <a href="/help-center" style={styles.backLink}>
                Return to Support Center
              </a>
            </div>
          </div>
        ) : !request ? (
          <div style={styles.card}>
            <div style={styles.error}>
              You do not have access to this ticket, or it no longer exists.
            </div>

            <div style={{ marginTop: "14px" }}>
              <a href="/help-center" style={styles.backLink}>
                Return to Support Center
              </a>
            </div>
          </div>
        ) : (
          <>
            <div style={styles.card}>
              <div style={styles.headerRow}>
                <div>
                  <div style={styles.requestId}>{request.publicId}</div>
                  <div style={styles.subject}>{request.subject}</div>
                </div>
                <div style={styles.statusBadge}>
                  {humanizeStatus(request.status)}
                </div>
              </div>

              <div style={styles.metaGrid}>
                <div>
                  <strong>Category:</strong> {humanizeIssueType(request.issueType)}
                </div>
                <div>
                  <strong>Priority:</strong> {humanizePriority(request.priority)}
                </div>
                <div>
                  <strong>Network:</strong> {request.network || "-"}
                </div>
                <div>
                  <strong>Submitted:</strong> {formatDate(request.createdAt)}
                </div>
                <div>
                  <strong>Last Updated:</strong> {formatDate(request.updatedAt)}
                </div>
                <div>
                  <strong>Deployment Reference:</strong>{" "}
                  {request.deploymentId || "-"}
                </div>
                <div>
                  <strong>Transaction Hash:</strong>{" "}
                  {request.transactionHash
                    ? getExplorerLabel(request.transactionHash)
                    : "-"}
                </div>
                <div>
                  <strong>Contract Address:</strong>{" "}
                  {request.contractAddress
                    ? getExplorerLabel(request.contractAddress)
                    : "-"}
                </div>
                <div>
                  <strong>Token Standard:</strong> {request.tokenStandard || "-"}
                </div>
                <div>
                  <strong>Token Name:</strong> {request.tokenName || "-"}
                </div>
                <div>
                  <strong>Token Symbol:</strong> {request.tokenSymbol || "-"}
                </div>
                <div>
                  <strong>Resolved At:</strong> {formatDate(request.resolvedAt)}
                </div>
              </div>

              <div style={styles.descriptionBlock}>
                <strong>Request Details</strong>
                <p style={styles.descriptionText}>{request.description}</p>
              </div>
            </div>

            <div style={styles.card}>
              <h2 style={styles.sectionTitle}>Conversation</h2>

              <div style={styles.messageList}>
                {request.messages?.length ? (
                  request.messages.map((message) => (
                    <div key={message.id} style={styles.messageItem}>
                      <div style={styles.messageMeta}>
                        <strong>
                          {humanizeSender(message.senderType, message.senderWallet, adminView)}
                        </strong>{" "}
                        • {formatDate(message.createdAt)}
                      </div>
                      <div style={styles.messageBody}>{message.body}</div>
                    </div>
                  ))
                ) : (
                  <p style={styles.muted}>No messages yet.</p>
                )}
              </div>
            </div>

            <div style={styles.card}>
              <h2 style={styles.sectionTitle}>
                {adminView ? "Admin Reply" : "Reply to Support"}
              </h2>

              {effectiveAdminView ? (
                <div style={styles.form}>
                  <div style={styles.adminControls}>
                    <div style={styles.adminControlBlock}>
                      <label style={styles.label}>Update Status</label>
                      <select
                        style={styles.select}
                        value={adminStatus}
                        onChange={(e) => setAdminStatus(e.target.value)}
                        disabled={adminBusy}
                      >
                        <option value="OPEN">Open</option>
                        <option value="IN_REVIEW">In Review</option>
                        <option value="WAITING_FOR_USER">Waiting for User</option>
                        <option value="RESOLVED">Resolved</option>
                        <option value="CLOSED">Closed</option>
                      </select>

                      <button
                        type="button"
                        style={{
                          ...styles.secondaryButton,
                          opacity: updatingStatus ? 0.7 : 1,
                          cursor: updatingStatus ? "not-allowed" : "pointer",
                        }}
                        disabled={adminBusy}
                        onClick={handleAdminStatusUpdate}
                      >
                        {updatingStatus ? "Updating..." : "Update Status"}
                      </button>
                    </div>
                  </div>

                  <textarea
                    style={styles.textarea}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Write an admin reply to the user..."
                  />

                  {replyError ? <div style={styles.error}>{replyError}</div> : null}
                  {adminActionError ? (
                    <div style={styles.error}>{adminActionError}</div>
                  ) : null}

                  <button
                    type="button"
                    style={{
                      ...styles.primaryButton,
                      opacity: replying ? 0.7 : 1,
                      cursor: replying ? "not-allowed" : "pointer",
                    }}
                    disabled={adminBusy || replyMessage.trim().length < 2}
                    onClick={handleReply}
                  >
                    {replying ? "Sending..." : "Send Admin Reply"}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleReply} style={styles.form}>
                  <textarea
                    style={styles.textarea}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Write your message here..."
                  />

                  {replyError ? <div style={styles.error}>{replyError}</div> : null}

                  <button
                    type="submit"
                    style={{
                      ...styles.primaryButton,
                      opacity: replying ? 0.7 : 1,
                      cursor: replying ? "not-allowed" : "pointer",
                    }}
                    disabled={adminBusy || replyMessage.trim().length < 2}
                  >
                    {replying ? "Sending..." : "Send Reply"}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #071120 0%, #0b1020 45%, #0c1327 100%)",
    color: "#fff",
    padding: "32px 18px 40px",
  },
  container: {
    maxWidth: "1040px",
    margin: "0 auto",
  },
  backLink: {
    color: "#8fb3ff",
    textDecoration: "none",
    display: "inline-block",
    marginBottom: "16px",
  },
  title: {
    marginTop: 0,
    marginBottom: "20px",
    fontSize: "34px",
    fontWeight: 800,
  },
  walletBox: {
    background: "rgba(20, 27, 52, 0.94)",
    border: "1px solid #253056",
    borderRadius: "20px",
    padding: "16px",
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  label: {
    fontSize: "14px",
    color: "#d7def7",
    fontWeight: 500,
  },
  input: {
    height: "46px",
    borderRadius: "14px",
    border: "1px solid #35406e",
    background: "#0f1833",
    color: "#fff",
    padding: "0 12px",
    outline: "none",
  },
  card: {
    background: "rgba(20, 27, 52, 0.94)",
    border: "1px solid #253056",
    borderRadius: "22px",
    padding: "22px",
    marginBottom: "20px",
    boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
    alignItems: "flex-start",
  },
  requestId: {
    color: "#8fb3ff",
    fontWeight: 700,
    marginBottom: "6px",
    fontSize: "13px",
  },
  subject: {
    fontSize: "24px",
    fontWeight: 800,
  },
  statusBadge: {
    alignSelf: "flex-start",
    fontSize: "12px",
    fontWeight: 700,
    color: "#d7e3ff",
    background: "#1b2a54",
    borderRadius: "999px",
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    color: "#c7d3f7",
    marginBottom: "18px",
    lineHeight: 1.5,
  },
  descriptionBlock: {
    background: "#0f1833",
    borderRadius: "16px",
    padding: "16px",
  },
  descriptionText: {
    marginBottom: 0,
    whiteSpace: "pre-wrap",
    color: "#dbe4ff",
    lineHeight: 1.6,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: "14px",
    fontSize: "22px",
    fontWeight: 700,
  },
  messageList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  messageItem: {
    background: "#0f1833",
    border: "1px solid #2b3563",
    borderRadius: "16px",
    padding: "16px",
  },
  messageMeta: {
    color: "#9bacd8",
    fontSize: "13px",
    marginBottom: "8px",
  },
  messageBody: {
    color: "#fff",
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  textarea: {
    minHeight: "140px",
    borderRadius: "14px",
    border: "1px solid #35406e",
    background: "#0f1833",
    color: "#fff",
    padding: "12px",
    outline: "none",
    resize: "vertical",
    lineHeight: 1.5,
  },
  primaryButton: {
    height: "46px",
    border: "none",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #4f7cff 0%, #6a92ff 100%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "15px",
  },
  secondaryButton: {
    height: "42px",
    border: "1px solid #3d4b80",
    borderRadius: "12px",
    background: "#182143",
    color: "#fff",
    fontWeight: 600,
  },
  adminControls: {
    display: "grid",
    gap: "16px",
  },
  adminControlBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  select: {
    height: "46px",
    borderRadius: "14px",
    border: "1px solid #35406e",
    background: "#0f1833",
    color: "#fff",
    padding: "0 12px",
    outline: "none",
  },
  infoBox: {
    background: "rgba(40, 56, 96, 0.45)",
    color: "#dbe4ff",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #32406f",
    fontSize: "14px",
    lineHeight: 1.6,
  },
  error: {
    background: "rgba(120, 25, 45, 0.30)",
    color: "#ffb4c0",
    padding: "12px",
    borderRadius: "12px",
    fontSize: "14px",
  },
  muted: {
    color: "#aab4d6",
  },
};